/**
 * Public viewer page reached via /share/:token/viewer.
 *
 * `/share/<token>/coverage` is the contract-version authority; a v2 verdict
 * mounts V2Player against the token's own `/share/<token>/v2/` playlists.
 * Those routes are additionally gated by `A5_V2_SHARE_TOKEN_ENABLED`; with
 * the flag off they 404 and the player's bounded retry surfaces that as an
 * error rather than an endless spinner. Only media-contract-v2 sessions are
 * playable — any other coverage verdict renders the unavailable state below.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Maximize, Minimize, Share2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useFullscreen } from '@/lib/fullscreen';
import V2Player from '../components/viewer/V2Player';
import { isSnapshotSettled } from '../components/viewer/snapshotStatus';
import { SessionCoverage } from '../types';

const RETIRED_FORMAT_MESSAGE =
  'This session was recorded under the retired legacy format and can no longer be played.';

interface ShareInfo {
  session_id: string;
  camera_id_scope: string | null;
  cameras: string[];
  master_url: string;
  gps_summary_url: string;
  label: string | null;
  expires_at: string | null;
}

interface ShareViewerPageProps {
  tokenOverride?: string;
}

export const ShareViewerPage: React.FC<ShareViewerPageProps> = ({ tokenOverride }) => {
  const token = tokenOverride;
  const [info, setInfo] = useState<ShareInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [v2Coverage, setV2Coverage] = useState<SessionCoverage | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { active: isFullscreen, toggle: toggleFullscreen } = useFullscreen(containerRef);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    // Mirrors `v2Coverage` state for the poll below to read without
    // retriggering this effect on every refresh.
    let latestV2Coverage: SessionCoverage | null = null;

    const probeV2Coverage = async (): Promise<SessionCoverage | null> => {
      try {
        const res = await fetch(`/share/${token}/coverage`);
        return res.ok ? ((await res.json()) as SessionCoverage) : null;
      } catch {
        return null;
      }
    };

    // A share link has no push channel — repolling is the only way an open
    // tab ever sees a healed snapshot or fresh availability bands. Stops once
    // the latest snapshot is settled; a failed fetch keeps the last good
    // coverage and retries next tick rather than disturbing a healthy player.
    const refreshV2Coverage = async () => {
      const coverage = await probeV2Coverage();
      if (cancelled || coverage?.media_contract_version !== 2) return;
      latestV2Coverage = coverage;
      setV2Coverage(coverage);
    };

    fetch(`/share/${token}/info`)
      .then((r) => {
        if (!r.ok) throw new Error(`share info ${r.status}`);
        return r.json();
      })
      .then(async (d: ShareInfo) => {
        if (cancelled) return;
        setInfo(d);
        const coverage = await probeV2Coverage();
        if (cancelled) return;
        if (coverage?.media_contract_version === 2) {
          latestV2Coverage = coverage;
          setV2Coverage(coverage);
          return;
        }
        setError(RETIRED_FORMAT_MESSAGE);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'failed to load share');
      });

    const id = window.setInterval(() => {
      if (latestV2Coverage && !isSnapshotSettled(latestV2Coverage)) void refreshV2Coverage();
    }, 5_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [token]);

  return (
    <div ref={containerRef} className="min-h-[100dvh] bg-bg-base p-2 text-fg sm:p-4">
      <div className="mb-2 flex items-center gap-2">
        <Share2 className="size-5 text-accent-purple" />
        <span className="font-mono text-accent-purple">Map Player · shared link</span>
        {info?.label && (
          <Badge variant="outline" className="font-mono text-fg-secondary">
            {info.label}
          </Badge>
        )}
        {info?.session_id && (
          <Badge variant="outline" className="font-mono text-fg-secondary">
            {info.session_id}
          </Badge>
        )}
        <div className="flex-1" />
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleFullscreen}
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              >
                {isFullscreen ? <Minimize /> : <Maximize />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {error && <p className="mt-8 font-mono text-status-error">{error}</p>}

      {!info && !error && (
        <div className="mt-16 flex flex-col items-center gap-3">
          <Spinner size={28} />
          <p className="text-fg-secondary">loading share…</p>
        </div>
      )}

      {info && v2Coverage && (
        <div className="mt-4 flex h-[calc(100dvh-120px)] flex-col">
          <V2Player
            masterUrl={`/share/${token}/v2/master.m3u8`}
            audioUnion={v2Coverage.audio_union}
            videoUnion={v2Coverage.video_union}
            snapshotRev={v2Coverage.snapshot_rev ?? null}
            audioExpected={v2Coverage.audio_expected ?? null}
            rotation={v2Coverage.rotation ?? null}
            // A share link is itself the deep link — no live-edge jump, no
            // saved-position lookup, always the same explicit entry point.
            entryIntent="explicit"
            chrome="minimal"
            // The page owns fullscreen (it takes the header along); routing the
            // playbar's Maximise here keeps one affordance rather than nesting
            // a second, player-only one inside it.
            onMaximize={toggleFullscreen}
            maximized={isFullscreen}
            onFatalError={(detail) => {
              // Unmount rather than stack a page error on top of the player's
              // own overlay. The coverage poll above keeps running in the
              // background, so a session that recovers remounts on its own —
              // a slow, visible retry rather than a tight loop.
              setV2Coverage(null);
              setError(`playback unavailable — ${detail}`);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ShareViewerPage;
